import React, { useState } from "react";
import DataTable from "react-data-table-component";
import { CSVLink } from "react-csv";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Swal from "sweetalert2";
import "bootstrap/dist/css/bootstrap.min.css";
import "remixicon/fonts/remixicon.css";

const PatientsList = ({
  patients = [],
  onEdit,
  onDelete,
  onAddPatient,
  onImportPatients,
  onManagePatientsProfile,
}) => {
  const [filterText, setFilterText] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  // ✅ Helper: Calculate Age from DOB
  const calculateAge = (dob) => {
    if (!dob) return "—";
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  // ✅ Add computed age to each patient
  // Bulk-imported records have no date of birth — they carry an age column instead.
  const patientsWithAge = patients.map((p) => ({
    ...p,
    age: p.dob ? calculateAge(p.dob) : p.age ?? "—",
  }));

  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: "1", // Default to Active
    gender: "",
    source: "", // "" = all, "1" = imported, "0" = added in app
    startDate: "",
    endDate: "",
  });

  // ✅ Filter by status, gender, date range, and text search
  const filteredPatients = patientsWithAge.filter((p) => {
    // 1. Text Search
    const fullName = `${p.first_name || ""} ${p.last_name || ""}`.toLowerCase();
    const mrNo = (p.mr_no || "").toLowerCase();
    const matchesText =
      fullName.includes(filterText.toLowerCase()) ||
      mrNo.includes(filterText.toLowerCase()) ||
      (p.phone && p.phone.toLowerCase().includes(filterText.toLowerCase()));

    if (!matchesText) return false;

    // 2. Status Filter
    if (filters.status !== "") {
      if (String(p.status) !== String(filters.status)) {
        return false;
      }
    }

    // 3. Gender Filter
    if (filters.gender !== "") {
      if ((p.gender || "").toLowerCase() !== filters.gender.toLowerCase()) {
        return false;
      }
    }

    // 4. Source Filter (imported vs. added in the app)
    if (filters.source !== "") {
      const isBulk = p.is_bulk ? "1" : "0";
      if (isBulk !== filters.source) return false;
    }

    // 5. Date Range Filter
    if (filters.startDate) {
      const pDate = new Date(p.created_at || p.created_date);
      const sDate = new Date(filters.startDate);
      sDate.setHours(0, 0, 0, 0);
      if (pDate < sDate) return false;
    }
    if (filters.endDate) {
      const pDate = new Date(p.created_at || p.created_date);
      const eDate = new Date(filters.endDate);
      eDate.setHours(23, 59, 59, 999);
      if (pDate > eDate) return false;
    }

    return true;
  });

  // ✅ Newest first: MR No year descending, then MR No sequence descending.
  // record_year is authoritative; fall back to the YY segment of TRH-YY-NNNNNN.
  const mrNoParts = (p) => {
    const parts = String(p.mr_no || "").split("-");
    const yearFromMrNo = parts.length >= 2 ? parseInt(parts[1], 10) : NaN;
    const year =
      Number(p.record_year) ||
      (Number.isNaN(yearFromMrNo) ? null : 2000 + yearFromMrNo);
    const sequence = parts.length >= 3 ? parseInt(parts[2], 10) : NaN;

    return { year, sequence: Number.isNaN(sequence) ? -1 : sequence };
  };

  const sortedPatients = [...filteredPatients].sort((a, b) => {
    const left = mrNoParts(a);
    const right = mrNoParts(b);

    // Records without a usable MR No sink to the bottom.
    if (left.year === null && right.year === null) return 0;
    if (left.year === null) return 1;
    if (right.year === null) return -1;

    if (left.year !== right.year) return right.year - left.year;
    return right.sequence - left.sequence;
  });

  // CSV mirrors the on-screen columns instead of dumping every raw field.
  const csvData = sortedPatients.map((p) => ({
    ID: p.id,
    "MR No": p.mr_no || "",
    Name: `${p.first_name || ""} ${p.last_name || ""}`.trim(),
    Phone: p.phone || "",
    Gender: p.gender || "",
    Age: p.age || "",
    Source: p.is_bulk ? "Imported" : "In-App",
  }));

  const totalRows = sortedPatients.length;
  const start = (currentPage - 1) * rowsPerPage + 1;
  const end = Math.min(currentPage * rowsPerPage, totalRows);

  // ✅ Custom Pagination
  const CustomPagination = ({
    onChangePage,
    onChangeRowsPerPage,
    currentPage,
    rowsPerPage,
    rowCount,
  }) => {
    const totalPages = Math.ceil(rowCount / rowsPerPage);

    return (
      <div className="d-flex justify-content-between align-items-center p-3 border-top">
        <div className="d-flex align-items-center gap-2">
          <span className="text-muted" style={{ fontSize: "0.875rem" }}>
            Rows per page:
          </span>
          <select
            className="form-select form-select-sm"
            style={{ width: "80px", fontSize: "0.875rem" }}
            value={rowsPerPage}
            onChange={(e) =>
              onChangeRowsPerPage(Number(e.target.value), currentPage)
            }
          >
            {[5, 10, 15, 20, 25].map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        <div className="d-flex align-items-center gap-1">
          <button
            className="btn btn-sm border"
            style={{
              borderRadius: "6px",
              height: "32px",
              width: "32px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#495057",
              border: "1px solid #dee2e6",
              backgroundColor: "#fff",
            }}
            onClick={() => onChangePage(currentPage - 1, rowCount)}
            disabled={currentPage === 1}
          >
            <i className="ri-arrow-left-s-line"></i>
          </button>

          <span className="mx-3 text-muted" style={{ fontSize: "0.875rem" }}>
            Page {currentPage} of {totalPages}
          </span>

          <button
            className="btn btn-sm border"
            style={{
              borderRadius: "6px",
              height: "32px",
              width: "32px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#495057",
              border: "1px solid #dee2e6",
              backgroundColor: "#fff",
            }}
            onClick={() => onChangePage(currentPage + 1, rowCount)}
            disabled={currentPage === totalPages}
          >
            <i className="ri-arrow-right-s-line"></i>
          </button>
        </div>
      </div>
    );
  };

  // ✅ Table Columns
  const columns = [
    {
      name: "ID",
      selector: (row) => row.id,
      sortable: true,
      width: "70px",
    },
    {
      name: "MR No",
      selector: (row) => row.mr_no || "—",
      sortable: true,
      width: "180px",
      cell: (row) => (
        <span className="text-nowrap">{row.mr_no || "—"}</span>
      ),
    },
    {
      name: "Name",
      selector: (row) =>
        `${row.first_name || ""} ${row.last_name || ""}`.trim(),
      sortable: true,
    },
    {
      name: "Phone",
      selector: (row) => row.phone || "—",
      sortable: true,
    },
    {
      name: "Gender",
      selector: (row) => row.gender || "—",
      sortable: true,
      width: "100px",
    },
    {
      name: "Age",
      selector: (row) => row.age || "—",
      sortable: true,
      width: "80px",
    },
    {
      name: "Source",
      selector: (row) => (row.is_bulk ? "Imported" : "In-App"),
      cell: (row) =>
        row.is_bulk ? (
          <span className="badge bg-label-info" title={`Imported record${row.record_year ? ` — ${row.record_year}` : ""}`}>
            Imported
          </span>
        ) : (
          <span className="badge bg-label-secondary">In-App</span>
        ),
      sortable: true,
      width: "110px",
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="d-flex justify-content-center gap-1">
          {onManagePatientsProfile && (
            <button
              className="btn btn-sm btn-outline-primary"
              title="Manage Active Client"
              onClick={() => onManagePatientsProfile(row)}
            >
              <i className="ri-user-line"></i>
            </button>
          )}
          {onEdit && (
            <button
              className="btn btn-sm btn-outline-primary"
              title="Edit Active Client"
              onClick={() => onEdit(row.id)}
            >
              <i className="ri-edit-line"></i>
            </button>
          )}
          {onDelete && (
            <button
              className="btn btn-sm btn-outline-danger"
              title="Delete Patient"
              onClick={() => onDelete(row.id)}
            >
              <i className="ri-delete-bin-line"></i>
            </button>
          )}
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "130px",
    },
  ];

  // ✅ Export Handlers
  const handleExportPDF = () => {
    const doc = new jsPDF();
    doc.text("Active Clients List", 14, 10);
    const tableColumn = ["ID", "MR No", "Name", "Phone", "Gender", "Age"];
    const tableRows = [];

    sortedPatients.forEach((p) => {
      const name = `${p.first_name || ""} ${p.last_name || ""}`.trim();
      tableRows.push([
        p.id,
        p.mr_no || "—",
        name,
        p.phone || "",
        p.gender || "",
        p.age || "",
      ]);
    });

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 20,
    });

    doc.save("patients_list.pdf");
  };

  const handleCopy = () => {
    const copyText = sortedPatients
      .map(
        (p) =>
          `${p.id}\t${p.mr_no || "—"}\t${p.first_name || ""} ${p.last_name || ""}\t${p.email || ""}\t${p.phone || ""}`
      )
      .join("\n");

    navigator.clipboard.writeText(copyText);

    Swal.fire({
      title: "Copied!",
      text: "Patient data copied to clipboard.",
      icon: "success",
      timer: 1500,
      showConfirmButton: false,
      position: "top",
      width: "280px",
    });
  };

  const handlePrint = () => {
    const printContent = `
      <html>
      <head>
        <title>Active Clients List</title>
        <style>
          body { font-family: Arial; margin: 20px; }
          table { width: 100%; border-collapse: collapse; }
          th, td { border: 1px solid #ccc; padding: 6px; text-align: left; }
          th { background: #f8f9fa; }
        </style>
      </head>
      <body>
        <h3>Active Clients List</h3>
        <table>
          <thead>
            <tr><th>ID</th><th>MR No</th><th>Name</th><th>Phone</th><th>Gender</th><th>Age</th></tr>
          </thead>
          <tbody>
            ${sortedPatients
        .map(
          (p) => `
              <tr>
                <td>${p.id}</td>
                <td>${p.mr_no || "—"}</td>
                <td>${p.first_name || ""} ${p.last_name || ""}</td>
                <td>${p.phone || ""}</td>
                <td>${p.gender || ""}</td>
                <td>${p.age || ""}</td>
              </tr>`
        )
        .join("")}
          </tbody>
        </table>
      </body>
      </html>`;
    const printWindow = window.open("", "_blank");
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <div>
      <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
        {/* Left: Export + Search + Filters */}
        <div className="d-flex align-items-center gap-2 position-relative">
          <div className="dropdown">
            <button
              className="btn btn-label-primary btn-sm dropdown-toggle"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <i className="ri-download-2-line me-1"></i> Export
            </button>
            <ul className="dropdown-menu shadow-sm">
              <li>
                <button className="dropdown-item" onClick={handleExportPDF}>
                  <i className="ri-file-pdf-line me-1 text-danger"></i> PDF
                </button>
              </li>
              <li>
                <CSVLink
                  data={csvData}
                  filename="patients_export.csv"
                  className="dropdown-item"
                >
                  <i className="ri-file-excel-line me-1 text-success"></i> CSV
                </CSVLink>
              </li>
              <li>
                <button className="dropdown-item" onClick={handleCopy}>
                  <i className="ri-file-copy-line me-1"></i> Copy
                </button>
              </li>
              <li>
                <button className="dropdown-item" onClick={handlePrint}>
                  <i className="ri-printer-line me-1"></i> Print
                </button>
              </li>
            </ul>
          </div>

          <input
            type="text"
            placeholder="Search patient..."
            className="form-control form-control-sm"
            style={{ width: "200px" }}
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />

          <button
            className={`btn btn-sm ${showFilters ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <i className="ri-filter-line me-1"></i> {showFilters ? 'Hide Filters' : 'Filters'}
          </button>
        </div>

        {/* Center: Info */}
        <h6 className="mb-0 text-center text-muted flex-grow-1 text-nowrap">
          Showing {start}-{end} of {totalRows} patients
        </h6>

        {/* Right: Import + Add Buttons */}
        <div className="d-flex align-items-center gap-2 flex-shrink-0">
          {onImportPatients && (
            <button
              className="btn btn-outline-success btn-sm d-inline-flex align-items-center text-nowrap"
              onClick={onImportPatients}
              title="Import active clients from the TRH Data Record excel sheet"
            >
              <i className="ri-file-excel-2-line me-1"></i> Import Excel
            </button>
          )}
          {onAddPatient && (
            <button
              className="btn btn-primary btn-sm d-inline-flex align-items-center text-nowrap"
              onClick={onAddPatient}
            >
              <i className="ri-user-add-line me-1"></i> Add Active Client
            </button>
          )}
        </div>
      </div>

      {/* Filters Accordion */}
      <div className={`collapse ${showFilters ? 'show' : ''}`}>
        <div className="px-4 py-3 border-bottom bg-light-subtle">
          <div className="row g-3">
            {/* Status Filter */}
            <div className="col-md-2">
              <label className="form-label small fw-bold">Status</label>
              <select
                className="form-select form-select-sm"
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              >
                <option value="">All Statuses</option>
                <option value="1">Active</option>
                <option value="0">Inactive</option>
                <option value="2">Pending</option>
              </select>
            </div>

            {/* Gender Filter */}
            <div className="col-md-2">
              <label className="form-label small fw-bold">Gender</label>
              <select
                className="form-select form-select-sm"
                value={filters.gender}
                onChange={(e) => setFilters(prev => ({ ...prev, gender: e.target.value }))}
              >
                <option value="">All Genders</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>

            {/* Source Filter */}
            <div className="col-md-2">
              <label className="form-label small fw-bold">Source</label>
              <select
                className="form-select form-select-sm"
                value={filters.source}
                onChange={(e) => setFilters(prev => ({ ...prev, source: e.target.value }))}
              >
                <option value="">All Sources</option>
                <option value="0">Added In-App</option>
                <option value="1">Imported (Excel)</option>
              </select>
            </div>

            {/* Start Date */}
            <div className="col-md-2">
              <label className="form-label small fw-bold">Start Date</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={filters.startDate}
                onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
              />
            </div>

            {/* End Date */}
            <div className="col-md-2">
              <label className="form-label small fw-bold">End Date</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={filters.endDate}
                onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
              />
            </div>

            {/* Reset Button */}
            <div className="col-md-2 d-flex align-items-end">
              <button
                className="btn btn-sm btn-outline-secondary w-100"
                onClick={() => setFilters({
                  status: "1", // Default back to Active
                  gender: "",
                  source: "",
                  startDate: "",
                  endDate: "",
                })}
              >
                <i className="ri-refresh-line me-1"></i> Reset
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card-body p-2">
        <DataTable
          columns={columns}
          data={sortedPatients}
          pagination
          paginationPerPage={rowsPerPage}
          paginationTotalRows={totalRows}
          onChangeRowsPerPage={setRowsPerPage}
          onChangePage={setCurrentPage}
          paginationComponent={CustomPagination}
          highlightOnHover
          striped
          responsive
          noDataComponent={<div className="p-3 text-muted">No patients found</div>}
        />
      </div>
    </div>
  );
};

export default PatientsList;
